import { createDocValueStore } from './docStore';
import type { RunProgress } from './openaiBlenderApi';

// A 3D GENERATOR'S STATE, OUTSIDE ITS PANEL: what is running, and what was chosen in it.
//
// Both lived in the panel, and the panel is thrown away whenever the selector moves — the whole
// product's when a part is opened, a part's when the product or "All parts" is. The run itself
// never stopped (it is one request, and nothing aborts it on the way out), but coming back to it
// showed a panel that did not know: no spinner, no Stop, the button offered again, and the
// photographs, the model and the passes back at their defaults as if nothing had been chosen.
//
// Module scope and keyed by document, like every other store that has to outlive a tab (see
// docStore). The keys name the generator inside a document:
//   product:<submodel>  the whole product in one colour — a run, and its ticked photographs
//   part:<item id>      one part — a run, and its ticked photographs
//   product / parts     the effort and the passes, one choice for each of the two panels

// A RUN IN FLIGHT, and how to call it off: the written engine's token and the request's abort
// (null for the moment between the press and the token). And how far it has got — the pass and
// the stage.
export type InFlight = {
  token: string | null;
  abort: AbortController | null;
  progress?: RunProgress;
  // WHEN IT WAS PRESSED — the clock on the button counts from here, so a panel thrown away and
  // drawn again mid-run carries on counting instead of starting again at nought.
  startedAt?: number;
};

const flights = createDocValueStore<Record<string, InFlight>>({});

export const useRunsInFlight = (docId: string) => flights.useValue(docId);

export const runInFlight = (docId: string, key: string): InFlight | null =>
  flights.get(docId)[key] ?? null;

// A run registered twice — once as it is pressed, again once it has a token to be stopped by —
// keeps the moment of the first.
export const startRun = (docId: string, key: string, flight: InFlight) =>
  flights.set(docId, {
    ...flights.get(docId),
    [key]: { ...flight, startedAt: runInFlight(docId, key)?.startedAt ?? Date.now() },
  });

// Kept with the run rather than in the panel, for the reason the run is: the panel showing it
// may be thrown away and come back mid-run.
export const setRunProgress = (docId: string, key: string, progress: RunProgress) => {
  const flight = runInFlight(docId, key);
  if (flight) flights.set(docId, { ...flights.get(docId), [key]: { ...flight, progress } });
};

export const endRun = (docId: string, key: string) => {
  const next = { ...flights.get(docId) };
  delete next[key];
  flights.set(docId, next);
};

// WHAT WAS CHOSEN. Each field absent until somebody chose it, so the panel's own default stands
// until then — the front photograph, the server's number of passes.
export type GeneratorChoice = {
  passes?: number; effort?: string; picked?: number[];
  // A piece of hardware's page crops ticked for its run, by key — see usePartStepCrops.
  pickedCrops?: string[];
  // Whether a product's written run is sent Phase 2's selected part crops — see productPartCrops.
  crops?: boolean;
};

const choices = createDocValueStore<Record<string, GeneratorChoice>>({});
const NONE: GeneratorChoice = {};
// One empty list for every panel with nothing ticked, so a memo keyed on the picks does not
// recompute on every render for a fresh `[]`.
export const NO_PICKS: number[] = [];
export const NO_CROP_PICKS: string[] = [];

export const getGeneratorChoice = (docId: string, key: string): GeneratorChoice =>
  choices.get(docId)[key] ?? NONE;

export const useGeneratorChoice = (docId: string, key: string): GeneratorChoice =>
  choices.useValue(docId)[key] ?? NONE;

export const setGeneratorChoice = (docId: string, key: string, patch: GeneratorChoice) =>
  choices.set(docId, { ...choices.get(docId), [key]: { ...getGeneratorChoice(docId, key), ...patch } });
